// NovaBank Phase 4 shared mock helpers — statement rows, notifications,
// available balance (balance − IPO blocks), debit gating (fraud freeze +
// re-KYC restriction), reference generators and the bank-clock runner that
// advances every time-based rule from mockNow (spec §2, §4, §9).
import { loadDb, saveDb, toPaise, type MockDb } from './db';
import { mockNow } from './clock';
import { mockId } from './util';
import type { StatementEntry } from '@/types/phase2.types';

// Statement categories (spec §2): charges for bank fees, bill for bill
// payments, investment for funds/deposits/IPO/NPS, interest for deposit/SGB
// interest. Existing payment categories (IMPS/UPI/...) stay as they are.
export type Phase4Category = 'charges' | 'bill' | 'investment' | 'interest';

let txnSeq = 0;
const nextId = (prefix: string): string => `${prefix}_${Date.now().toString(36)}_${(txnSeq++).toString(36)}`;

/** Append a statement row and refresh the balance. Signed rupees. */
export const addStatementRow = (
  db: MockDb,
  input: { description: string; amountRupees: number; category: string; reference?: string; counterparty?: string },
  at?: number,
): StatementEntry => {
  const row: StatementEntry = {
    id: nextId('txn'),
    date: at ?? mockNow(db),
    description: input.description,
    counterparty: input.counterparty,
    amountRupees: input.amountRupees,
    category: input.category,
    reference: input.reference,
  };
  db.ledger.unshift(row);
  db.balancePaise += toPaise(input.amountRupees);
  return row;
};

export const addNotification = (db: MockDb, title: string, body: string, at?: number): void => {
  db.phase4.notifications.unshift({ id: nextId('ntf'), at: at ?? mockNow(db), title, body, read: false });
  if (db.phase4.notifications.length > 100) db.phase4.notifications.length = 100;
};

// ---------- references (spec §9: prefix + 8 random digits) ----------

export type RefPrefix = 'CB' | 'SP' | 'SR' | 'CMP' | 'FRD' | 'LK' | 'BP' | 'BCC' | 'MD' | 'MFO' | 'IPO' | 'CC' | 'CARD';

export const makeReference = (prefix: RefPrefix): string =>
  `${prefix}${String(Math.floor(10_000_000 + Math.random() * 89_999_999))}`;

// ---------- balance / debit gating ----------

/** Available = balance − IPO blocks (spec §7.3: blocked money can't be spent). */
export const availableBalancePaise = (db: MockDb): number =>
  db.balancePaise - db.phase4.ipoBlockedPaise;

export interface DebitGate {
  ok: boolean;
  message?: string;
}

const REKYC_RESTRICT_MSG =
  'Re-KYC is overdue. Debits are paused until you complete Re-KYC; credits still come in.';
const FRAUD_FREEZE_MSG = 'Debits are frozen on this account.';

/** True when re-KYC restriction currently blocks debits (spec §5.2). */
export const rekycRestrictsDebits = (db: MockDb): boolean => {
  const { rekyc } = db.phase4;
  const now = mockNow(db);
  if (now <= rekyc.dueAt) return false;
  if (rekyc.risk === 'LOW') {
    // Low risk: usable until the later of due+1y and 30 Jun 2026.
    const lowLimit = Math.max(rekyc.dueAt + 365 * 24 * 3_600_000, Date.UTC(2026, 5, 30));
    return now > lowLimit;
  }
  // Medium/High: debits blocked at due + 90 days.
  return now > rekyc.dueAt + 90 * 24 * 3_600_000;
};

/**
 * Gate every Phase 4 debit. Order: fraud freeze → re-KYC restriction →
 * available balance. Returns the message to show on failure.
 */
export const assertDebitAllowed = (db: MockDb, amountRupees: number): DebitGate => {
  if (db.limits.debitsFrozen) return { ok: false, message: FRAUD_FREEZE_MSG };
  if (rekycRestrictsDebits(db)) return { ok: false, message: REKYC_RESTRICT_MSG };
  if (amountRupees > 0 && availableBalancePaise(db) < toPaise(amountRupees)) {
    return { ok: false, message: 'Insufficient balance.' };
  }
  return { ok: true };
};

export { FRAUD_FREEZE_MSG, REKYC_RESTRICT_MSG };

// Convenience re-export so handlers can stamp ids consistently.
export { mockId, mockNow };

// ---------- bank clock runner ----------

/**
 * runBankClock advances every time-based Phase 4 rule to "now". Each module's
 * screen calls it on load; the dev test panel calls it after moving the clock.
 * It is idempotent — safe to call repeatedly.
 *
 * Time-based owners register their own advance step here so the ordering is
 * explicit and single-sourced. Modules that own a clock rule append to this
 * list from their handler file (kept here to avoid import cycles).
 */
export type ClockStep = (db: MockDb, now: number) => void;
const clockSteps: ClockStep[] = [];

export const registerClockStep = (step: ClockStep): void => {
  clockSteps.push(step);
};

export const runBankClock = (): void => {
  const db = loadDb();
  const now = mockNow(db);
  for (const step of clockSteps) step(db, now);
  saveDb(db);
};
